/**
 * Billing > Rate cards > one card (Phase 21 / Task 21.7b): its versions as a timeline, each
 * with its lines; a DRAFT can be edited, published or deleted; publishing is one-way and
 * freezes the prices (migration 0138's publish lock).
 *
 *   GET/PATCH /v1/platform/billing/rate-cards/:id
 *   POST      /v1/platform/billing/rate-cards/:id/versions        (copy of the latest, or empty)
 *   PUT       /v1/platform/billing/rate-card-versions/:versionId  (full line replace)
 *   POST      /v1/platform/billing/rate-card-versions/:versionId/publish
 *   DELETE    /v1/platform/billing/rate-card-versions/:versionId  (DRAFT only)
 */
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackTo } from '../../lib/url-state';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  useBillingToday,
  useCreateVersion,
  useDeleteVersion,
  usePatchRateCard,
  usePublishVersion,
  useRateCard,
  useSaveVersion,
  type RateCardDetail,
  type RateCardVersion,
} from './billing-hooks';
import { billingErrorText, templateLabel } from './billing-format';
import type { FeatureTemplate } from '@lodex-pro/shared/features';
import { TemplateSelect } from './RateCardsPage';
import {
  CHARGE_KINDS,
  CHARGE_KIND_LABELS,
  METER_LABELS,
  draftErrors,
  emptyBand,
  emptyLine,
  lineToDraft,
  linePriceText,
  versionBody,
  type LineDraft,
} from './rate-card-draft';

const box: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '16px',
  marginBottom: '14px',
};

function CardHeader({ card }: { card: RateCardDetail }): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState(card.name);
  const [description, setDescription] = React.useState(card.description ?? '');
  const [template, setTemplate] = React.useState<FeatureTemplate | ''>(card.featureTemplate ?? '');
  const patch = usePatchRateCard(card.id, {
    onSuccess: () => {
      setEditing(false);
      toast.success('Rate card saved.');
    },
    onError: (err) => toast.error(billingErrorText(err, 'Could not save the rate card.')),
  });
  if (!editing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '8px 0 4px' }}>
            {card.name} {card.isArchived && <Pill tone="muted" label="Archived" />}
          </h2>
          <p style={{ ...subText, margin: 0 }}>{card.description ?? 'No description.'}</p>
          {card.tenantType === 'HAULER' && (
            <p style={{ ...subText, margin: '2px 0 0' }} data-testid="card-template">
              Sells hauler plan:{' '}
              {card.featureTemplate === null ? 'not named' : templateLabel(card.featureTemplate)}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type="button"
            variant="outline"
            onClick={(): void => {
              setName(card.name);
              setDescription(card.description ?? '');
              setTemplate(card.featureTemplate ?? '');
              setEditing(true);
            }}
          >
            Edit
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={(): void => patch.mutate({ isArchived: !card.isArchived })}
          >
            {card.isArchived ? 'Restore' : 'Archive'}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
        if (name.trim() === '') return;
        patch.mutate({
          name: name.trim(),
          description: description.trim() === '' ? null : description.trim(),
          ...(card.tenantType === 'HAULER'
            ? { featureTemplate: template === '' ? null : template }
            : {}),
        });
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '520px' }}
    >
      <FormRow label="Name" htmlFor="rcd-name" required>
        <input
          id="rcd-name"
          value={name}
          onChange={(e): void => setName(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      {card.tenantType === 'HAULER' && (
        <FormRow label="Sells hauler plan" htmlFor="rcd-template">
          <TemplateSelect id="rcd-template" value={template} onChange={setTemplate} />
        </FormRow>
      )}
      <FormRow label="Description" htmlFor="rcd-desc">
        <input
          id="rcd-desc"
          value={description}
          onChange={(e): void => setDescription(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button type="submit" disabled={name.trim() === '' || patch.isPending}>
          Save
        </Button>
        <Button type="button" variant="ghost" onClick={(): void => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function LinesTable({ version }: { version: RateCardVersion }): React.JSX.Element {
  if (version.lines.length === 0) return <p style={subText}>No lines.</p>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
      <thead>
        <tr style={{ textAlign: 'left', ...subText }}>
          <th style={{ padding: '4px 6px' }}>Code</th>
          <th style={{ padding: '4px 6px' }}>Description</th>
          <th style={{ padding: '4px 6px' }}>Kind</th>
          <th style={{ padding: '4px 6px' }}>Price</th>
          <th style={{ padding: '4px 6px' }} />
        </tr>
      </thead>
      <tbody>
        {version.lines.map((l) => (
          <tr key={l.id} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
            <td style={{ padding: '6px' }}>{l.code}</td>
            <td style={{ padding: '6px' }}>{l.description}</td>
            <td style={{ padding: '6px' }}>{CHARGE_KIND_LABELS[l.chargeKind]}</td>
            <td style={{ padding: '6px' }}>{linePriceText(l)}</td>
            <td style={{ padding: '6px' }}>{l.isAddOn && <Pill tone="accent" label="Add-on" />}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LineEditor({
  line,
  index,
  onChange,
  onRemove,
}: {
  line: LineDraft;
  index: number;
  onChange: (l: LineDraft) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const id = (f: string): string => `line-${String(index)}-${f}`;
  const set = <K extends keyof LineDraft>(k: K, v: LineDraft[K]): void =>
    onChange({ ...line, [k]: v });
  const usesMeter = line.chargeKind === 'PER_UNIT' || line.chargeKind === 'TIERED_BAND';
  return (
    <div
      data-testid={`line-editor-${String(index)}`}
      style={{
        borderRadius: '12px',
        backgroundColor: 'var(--color-bg-tertiary)',
        padding: '10px',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '8px' }}>
        <FormRow label="Code" htmlFor={id('code')}>
          <input
            id={id('code')}
            value={line.code}
            onChange={(e): void => set('code', e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Description" htmlFor={id('desc')}>
          <input
            id={id('desc')}
            value={line.description}
            onChange={(e): void => set('description', e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Kind" htmlFor={id('kind')}>
          <select
            id={id('kind')}
            value={line.chargeKind}
            onChange={(e): void => set('chargeKind', e.target.value as LineDraft['chargeKind'])}
            style={fieldStyle}
          >
            {CHARGE_KINDS.map((k) => (
              <option key={k} value={k}>
                {CHARGE_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </FormRow>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
        {usesMeter && (
          <FormRow label="Meter" htmlFor={id('meter')}>
            <select
              id={id('meter')}
              value={line.meter}
              onChange={(e): void => set('meter', e.target.value)}
              style={fieldStyle}
            >
              <option value="">Choose…</option>
              {Object.entries(METER_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </FormRow>
        )}
        {line.chargeKind === 'PER_MODULE' && (
          <FormRow label="Module code" htmlFor={id('module')}>
            <input
              id={id('module')}
              value={line.moduleCode}
              onChange={(e): void => set('moduleCode', e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
        )}
        <FormRow label="Unit price ($)" htmlFor={id('price')}>
          <input
            id={id('price')}
            value={line.unitPrice}
            onChange={(e): void => set('unitPrice', e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        {line.chargeKind === 'PER_UNIT' && (
          <FormRow label="Included free" htmlFor={id('included')}>
            <input
              id={id('included')}
              value={line.includedQuantity}
              onChange={(e): void => set('includedQuantity', e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
        )}
      </div>
      {line.chargeKind === 'TIERED_BAND' && (
        <div style={{ marginTop: '8px' }}>
          {line.bands.map((b, j) => (
            <div
              key={j}
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '6px' }}
            >
              <input
                aria-label={`Band ${String(j + 1)} from`}
                value={b.fromQty}
                onChange={(e): void =>
                  set(
                    'bands',
                    line.bands.map((x, k) => (k === j ? { ...x, fromQty: e.target.value } : x)),
                  )
                }
                style={fieldStyle}
              />
              <input
                aria-label={`Band ${String(j + 1)} to`}
                placeholder="no limit"
                value={b.toQty}
                onChange={(e): void =>
                  set(
                    'bands',
                    line.bands.map((x, k) => (k === j ? { ...x, toQty: e.target.value } : x)),
                  )
                }
                style={fieldStyle}
              />
              <input
                aria-label={`Band ${String(j + 1)} price`}
                value={b.price}
                onChange={(e): void =>
                  set(
                    'bands',
                    line.bands.map((x, k) => (k === j ? { ...x, price: e.target.value } : x)),
                  )
                }
                style={fieldStyle}
              />
              <Button
                type="button"
                variant="ghost"
                onClick={(): void =>
                  set(
                    'bands',
                    line.bands.filter((_, k) => k !== j),
                  )
                }
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={(): void => set('bands', [...line.bands, emptyBand()])}
          >
            Add band
          </Button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
        <label style={{ ...subText, display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={line.isAddOn}
            onChange={(e): void => set('isAddOn', e.target.checked)}
          />
          Add-on (billed only when the subscription lists it)
        </label>
        <Button type="button" variant="ghost" onClick={onRemove}>
          Remove line
        </Button>
      </div>
    </div>
  );
}

function DraftEditor({
  version,
  onDone,
}: {
  version: RateCardVersion;
  onDone: () => void;
}): React.JSX.Element {
  const [effectiveFrom, setEffectiveFrom] = React.useState(version.effectiveFrom);
  const [notes, setNotes] = React.useState(version.notes ?? '');
  const [lines, setLines] = React.useState<LineDraft[]>(() => version.lines.map(lineToDraft));
  const save = useSaveVersion(version.id, {
    onSuccess: () => {
      toast.success('Draft saved.');
      onDone();
    },
    onError: (err) => toast.error(billingErrorText(err, 'Could not save the draft.')),
  });
  const errors = draftErrors(lines);
  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
        if (errors.length === 0 && effectiveFrom !== '') {
          save.mutate(versionBody(effectiveFrom, notes, lines));
        }
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px' }}>
        <FormRow label="In force from" htmlFor={`v-${version.id}-from`}>
          <input
            id={`v-${version.id}-from`}
            type="date"
            value={effectiveFrom}
            onChange={(e): void => setEffectiveFrom(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Notes (not printed)" htmlFor={`v-${version.id}-notes`}>
          <input
            className="keep-case"
            id={`v-${version.id}-notes`}
            value={notes}
            onChange={(e): void => setNotes(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
      </div>
      {lines.map((l, i) => (
        <LineEditor
          key={i}
          line={l}
          index={i}
          onChange={(nl): void => setLines(lines.map((x, k) => (k === i ? nl : x)))}
          onRemove={(): void => setLines(lines.filter((_, k) => k !== i))}
        />
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          onClick={(): void => setLines([...lines, emptyLine()])}
        >
          Add line
        </Button>
      </div>
      {errors.length > 0 && (
        <ul
          role="alert"
          data-testid="draft-errors"
          style={{ color: 'var(--color-danger)', fontSize: '12px' }}
        >
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          type="submit"
          disabled={errors.length > 0 || effectiveFrom === '' || save.isPending}
        >
          Save draft
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function VersionCard({ version }: { version: RateCardVersion }): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const [confirmPublish, setConfirmPublish] = React.useState(false);
  const publish = usePublishVersion(version.id, {
    onSuccess: () => {
      setConfirmPublish(false);
      toast.success('Published. These prices can no longer change.');
    },
    onError: (err) => toast.error(billingErrorText(err, 'Could not publish.')),
  });
  const remove = useDeleteVersion(version.id, {
    onSuccess: () => toast.success('Draft deleted.'),
    onError: (err) => toast.error(billingErrorText(err, 'Could not delete the draft.')),
  });
  const draft = version.status === 'DRAFT';
  return (
    <section style={box} data-testid={`version-${String(version.versionNumber)}`}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
        }}
      >
        <div>
          <strong>Version {version.versionNumber}</strong>{' '}
          <Pill tone={draft ? 'warning' : 'positive'} label={draft ? 'Draft' : 'Published'} />{' '}
          <span style={subText}>
            in force from {version.effectiveFrom}
            {version.publishedAt !== null &&
              `, published ${new Date(version.publishedAt).toLocaleDateString()}`}
          </span>
        </div>
        {draft && !editing && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button type="button" variant="outline" onClick={(): void => setEditing(true)}>
              Edit
            </Button>
            {confirmPublish ? (
              <>
                <Button
                  type="button"
                  disabled={publish.isPending || version.lines.length === 0}
                  onClick={(): void => publish.mutate({})}
                >
                  Confirm publish
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={(): void => setConfirmPublish(false)}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                type="button"
                disabled={version.lines.length === 0}
                onClick={(): void => setConfirmPublish(true)}
              >
                Publish
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={(): void => remove.mutate({})}>
              Delete draft
            </Button>
          </div>
        )}
      </div>
      {confirmPublish && (
        <p role="alert" style={{ ...subText, marginTop: 0 }}>
          Publishing is final: these prices can never be edited again. A change later is a new
          version.
        </p>
      )}
      {editing ? (
        <DraftEditor version={version} onDone={(): void => setEditing(false)} />
      ) : (
        <LinesTable version={version} />
      )}
    </section>
  );
}

function NewVersion({ card, today }: { card: RateCardDetail; today: string }): React.JSX.Element {
  const latest = card.versions[card.versions.length - 1];
  const [effectiveFrom, setEffectiveFrom] = React.useState(today);
  const [copy, setCopy] = React.useState(latest !== undefined);
  const create = useCreateVersion(card.id, {
    onSuccess: () => toast.success('Draft version created.'),
    onError: (err) => toast.error(billingErrorText(err, 'Could not create the version.')),
  });
  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
        if (effectiveFrom === '') return;
        create.mutate({
          effectiveFrom,
          ...(copy && latest !== undefined ? { copyFromVersionId: latest.id } : {}),
        });
      }}
      style={{ ...box, display: 'flex', gap: '10px', alignItems: 'end', flexWrap: 'wrap' }}
    >
      <FormRow label="New version in force from" htmlFor="nv-from">
        <input
          id="nv-from"
          type="date"
          value={effectiveFrom}
          onChange={(e): void => setEffectiveFrom(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      {latest !== undefined && (
        <label style={{ ...subText, display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input type="checkbox" checked={copy} onChange={(e): void => setCopy(e.target.checked)} />
          Start from version {latest.versionNumber}&apos;s lines
        </label>
      )}
      <Button type="submit" disabled={effectiveFrom === '' || create.isPending || card.isArchived}>
        New draft version
      </Button>
    </form>
  );
}

export function RateCardDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // OPEN-191 — Back keeps the list's Archived toggle.
  const backTo = useBackTo('/billing/rate-cards');
  const { data, isLoading, isError } = useRateCard(id);
  const today = useBillingToday();

  if (isLoading) return <p style={subText}>Loading…</p>;
  if (isError || data === undefined) {
    return (
      <p role="alert" style={{ color: 'var(--color-danger)' }}>
        Could not load this rate card.
      </p>
    );
  }
  const card = data.rateCard;
  const newestFirst = [...card.versions].sort((a, b) => b.versionNumber - a.versionNumber);
  return (
    <div style={{ maxWidth: '920px' }}>
      <Button
        type="button"
        variant="ghost"
        onClick={(): void => {
          void navigate(backTo);
        }}
      >
        <ArrowLeft size={16} aria-hidden="true" /> Rate cards
      </Button>
      <CardHeader card={card} />
      <div style={{ marginTop: '16px' }}>
        {today !== null && <NewVersion key={today} card={card} today={today} />}
        {newestFirst.length === 0 && <p style={subText}>No versions yet.</p>}
        {newestFirst.map((v) => (
          <VersionCard key={v.id} version={v} />
        ))}
      </div>
    </div>
  );
}
